import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import {
  Globe,
  Zap,
  Shield,
  BarChart3,
  Brain,
  Bell,
  Briefcase,
  ArrowRight,
  Check,
  Building,
  Users,
  Palette,
  MessageCircle,
  Bot,
  User,
  Send,
  CheckCircle2,
  Eye,
  Target,
  Landmark,
  Library,
  Lightbulb,
  Search,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { eodhd } from '../services/eodhd';
import { getCached, setCache } from '../services/cache';
import { fetchPlanPrices } from '../services/planPrices';
import type { RealTimePrice } from '../types';
import BrandVideoHero from '../components/landing/BrandVideoHero';

// Splits an API display string ("$99.00/mo") into the headline + suffix the
// pricing-card markup expects ("$99", "/month"). Integer dollars drop the ".00".
function splitPriceDisplay(display: string): { big: string; suffix: string } {
  const match = display.match(/^\$(\d+)(\.\d+)?\/mo$/);
  if (!match) return { big: display, suffix: '' };
  const cents = match[2] ?? '';
  const big = cents === '' || cents === '.00' ? `$${match[1]}` : `$${match[1]}${cents}`;
  return { big, suffix: '/month' };
}

/* ═══════════════════ GOLD M LOGO ═══════════════════ */

const GoldMLogo: React.FC<{ size?: number }> = ({ size = 28 }) => {
  const id = `mg${size}`;
  const pad = size * 0.12;
  const inner = size - pad * 2;
  const pts = [
    [0.23, 0.88], [0.23, 0.40], [0.42, 0.65], [0.50, 0.50],
    [0.58, 0.35], [0.77, 0.62], [0.77, 0.88],
  ].map(([x, y]) => `${(x * inner + pad).toFixed(2)},${(y * inner + pad).toFixed(2)}`).join(' ');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', flexShrink: 0 }}>
      <defs>
        <linearGradient id={`${id}-stroke`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8C96A" />
          <stop offset="100%" stopColor="#C9A84C" />
        </linearGradient>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1A1628" />
          <stop offset="100%" stopColor="#0C0B18" />
        </linearGradient>
      </defs>
      <rect width={size} height={size} rx={size * 0.22} fill={`url(#${id}-bg)`} />
      <rect width={size} height={size} rx={size * 0.22} fill="none" stroke="#C9A84C" strokeWidth="0.8" opacity="0.4" />
      <polyline points={pts} stroke={`url(#${id}-stroke)`} strokeWidth={size * 0.095} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

/* ═══════════════════ TYPES ═══════════════════ */

interface MarketData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
}

/* ═══════════════════ ANIMATIONS ═══════════════════ */

const STYLE_ID = 'landing-v3';

const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes l-fade-up {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes l-gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes l-float-1 {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(20px, -15px); }
    }
    @keyframes l-float-2 {
      0%, 100% { transform: translate(0, 0); }
      50% { transform: translate(-15px, 10px); }
    }
    .l-reveal { opacity: 0; transform: translateY(40px); }
    .l-visible { animation: l-fade-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    .l-delay-1.l-visible { animation-delay: 0.12s; }
    .l-delay-2.l-visible { animation-delay: 0.24s; }
    .l-gradient-text-dark {
      background: linear-gradient(135deg, #2dd4bf, #818cf8, #f472b6, #2dd4bf);
      background-size: 300% 300%;
      animation: l-gradient 8s ease infinite;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .l-gradient-text-light {
      background: linear-gradient(135deg, #0d9488, #6366f1, #db2777, #0d9488);
      background-size: 300% 300%;
      animation: l-gradient 8s ease infinite;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .l-orb-1 { animation: l-float-1 20s ease-in-out infinite; }
    .l-orb-2 { animation: l-float-2 25s ease-in-out infinite; }
    .l-card-lift {
      transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .l-card-lift:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.08);
    }
    .l-nav-scroll {
      transition: background-color 0.3s, border-color 0.3s, backdrop-filter 0.3s;
    }
  `;
  document.head.appendChild(s);
};

/* ═══════════════════ HOOKS ═══════════════════ */

const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('l-visible');
          e.target.querySelectorAll('.l-reveal').forEach((c) => c.classList.add('l-visible'));
        }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`l-reveal ${delay ? `l-delay-${delay}` : ''} ${className}`}>{children}</div>;
};

/* Use scroll position to toggle nav style */
const useScrolled = (threshold = 50) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', h, { passive: true });
    h();
    return () => window.removeEventListener('scroll', h);
  }, [threshold]);
  return scrolled;
};

/* ═══════════════════ MOCKUPS ═══════════════════ */

/** Dashboard mockup — dark card style for hero */
const DashboardMockup: React.FC<{ marketData: MarketData[]; loading: boolean }> = ({ marketData, loading }) => (
  <div className="rounded-2xl border border-white/[0.08] bg-[#111113] overflow-hidden shadow-2xl shadow-black/40">
    <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-1.5">
      <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-2 text-[11px] text-slate-500">Dashboard — my investments</span>
    </div>
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        <span className="text-[10px] text-slate-600">LIVE</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {(loading ? Array(6).fill(null) : marketData.slice(0, 6)).map((item, i) => (
          <div key={i} className="bg-white/[0.03] border border-white/[0.05] rounded-lg p-2.5">
            {item ? (
              <>
                <p className="text-[9px] text-slate-500 truncate">{item.name}</p>
                <p className="text-sm font-bold text-white">{item.symbol.includes('FOREX') ? item.price.toFixed(4) : item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span className={`text-[10px] font-medium ${item.changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>{item.changePercent >= 0 ? '+' : ''}{item.changePercent.toFixed(2)}%</span>
              </>
            ) : (
              <div className="animate-pulse space-y-1"><div className="h-2 w-10 bg-white/[0.05] rounded" /><div className="h-3.5 w-14 bg-white/[0.05] rounded" /><div className="h-2 w-8 bg-white/[0.05] rounded" /></div>
            )}
          </div>
        ))}
      </div>
      {/* Mini chart */}
      <div className="bg-white/[0.03] border border-white/[0.05] rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] text-white font-semibold">XAUUSD</span>
          <span className="text-[10px] text-green-400 font-medium">+0.34%</span>
        </div>
        <div className="flex items-end gap-[2px] h-16">
          {[35,42,38,50,45,55,48,60,52,65,58,70,62,72,68,75,70,78,72,80,76,82,78,85].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-teal-500/50 to-teal-400/10" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/** AI Chat mockup — LIGHT card style for white section */
const AIChatMockupLight: React.FC = () => (
  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-xl shadow-gray-200/50">
    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-1.5">
      <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
      <MessageCircle size={12} className="ml-2 text-teal-600" />
      <span className="text-[11px] text-gray-400">Expert Chat</span>
    </div>
    <div className="p-4 space-y-3">
      <div className="flex gap-2.5 items-start">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0"><User size={10} className="text-white" /></div>
        <div className="bg-gray-50 rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%]">
          <p className="text-xs text-gray-700">Is GOLD a buy right now?</p>
        </div>
      </div>
      <div className="flex gap-2.5 items-start">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0"><Bot size={10} className="text-white" /></div>
        <div className="space-y-1.5 max-w-[90%]">
          <div className="flex flex-wrap gap-1">
            {['Market Data', 'S/R Zones', 'Risk Rules'].map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-teal-50 text-teal-600 border border-teal-100">{t} <CheckCircle2 size={7} className="inline" /></span>
            ))}
          </div>
          <div className="bg-gray-50 rounded-xl rounded-tl-sm px-3 py-2">
            <p className="text-xs text-gray-700"><span className="text-teal-600 font-semibold">XAUUSD @ $2,347</span> — Testing daily support. H4 trend bullish, risk 1.2% of account.</p>
            <p className="text-xs text-emerald-600 mt-1 font-medium">Signal: BUY — 84% confidence</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
        <span className="text-xs text-gray-400 flex-1">Ask about any asset...</span>
        <Send size={12} className="text-teal-500" />
      </div>
    </div>
  </div>
);

/* ═══════════════════ CONSTANTS ═══════════════════ */

const LANDING_MARKET_CACHE_KEY = 'landing_market_data';
const LANDING_CACHE_TTL = 2 * 60 * 60 * 1000;

/* ═══════════════════ MAIN ═══════════════════ */

export default function Landing() {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const scrolled = useScrolled(80);

  const { data: planPrices } = useQuery({
    queryKey: ['plan-prices'],
    queryFn: fetchPlanPrices,
    staleTime: 60_000,
    retry: 1,
  });
  // Skeleton when neither the live API nor the localStorage cache yielded a
  // value. Hardcoded fallbacks would go stale whenever admin updates prices.
  const proPrice     = planPrices?.pro?.display     ? splitPriceDisplay(planPrices.pro.display)     : null;
  const premiumPrice = planPrices?.premium?.display ? splitPriceDisplay(planPrices.premium.display) : null;

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    loadMarketData();
    const interval = setInterval(loadMarketData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadMarketData = async () => {
    const cached = getCached<MarketData[]>(LANDING_MARKET_CACHE_KEY);
    if (cached && cached.length > 0) { setMarketData(cached); setLoadingMarkets(false); return; }
    try {
      const symbols = [
        { symbol: 'NPN.JSE', name: 'Naspers' }, { symbol: 'SOL.JSE', name: 'Sasol' },
        { symbol: 'AGL.JSE', name: 'Anglo American' }, { symbol: 'AAPL.US', name: 'Apple' },
        { symbol: 'MSFT.US', name: 'Microsoft' }, { symbol: 'USDZAR.FOREX', name: 'USD/ZAR' },
      ];
      const results = await Promise.allSettled(
        symbols.map(async (s) => {
          const data: RealTimePrice = await eodhd.getRealTimePrice(s.symbol);
          return { symbol: s.symbol, name: s.name, price: data.close || 0, change: data.change || 0, changePercent: data.change_percent ?? data.change_p ?? 0 };
        })
      );
      const ok = results.filter((r): r is PromiseFulfilledResult<MarketData> => r.status === 'fulfilled').map((r) => r.value);
      if (ok.length > 0) setCache(LANDING_MARKET_CACHE_KEY, ok, LANDING_CACHE_TTL);
      setMarketData(ok);
    } catch (e) { console.error('Error loading market data:', e); }
    finally { setLoadingMarkets(false); }
  };

  return (
    <div className="selection:bg-teal-500/30 overflow-x-hidden">
      <SEO
        title="Expert-Built Investing & Finance Dashboard"
        description="My Investments is a smarter finance dashboard built by our team for traders and investors. Get real-time market data, expert, data-driven trading signals, portfolio analytics, and expert insights — all in one place."
        ogType="website"
        canonical="https://my-investments.io/welcome"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "My Investments",
          "url": "https://my-investments.io",
          "description": "Expert-built finance dashboard for traders and investors",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://my-investments.io/screener?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "My Investments",
          "url": "https://my-investments.io",
          "logo": "https://my-investments.io/favicon.svg",
          "description": "Expert, data-driven trading signals, market analytics, and portfolio management platform",
          "sameAs": []
        })}</script>
      </Helmet>

      {/* ══════════════ NAV — adapts to section bg ══════════════ */}
      <nav className={`l-nav-scroll fixed top-0 left-0 right-0 z-50 h-12 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-2xl backdrop-saturate-150 border-b border-gray-200/60'
          : 'bg-black/60 backdrop-blur-2xl border-b border-white/[0.04]'
      }`}>
        <div className="max-w-6xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GoldMLogo size={28} />
            <span className={`text-sm font-bold tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>my investments</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/releases" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium transition-colors ${
              scrolled
                ? 'bg-teal-50 border border-teal-200/60 text-teal-700 hover:bg-teal-100'
                : 'bg-white/[0.04] border border-white/[0.06] text-teal-400 hover:bg-white/[0.08]'
            }`}>
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />v2.0
            </Link>
            <Link to="/mcp" className={`text-sm transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-400 hover:text-white'}`}>MCP</Link>
            <Link to="/login" className={`text-sm hidden sm:inline transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-slate-400 hover:text-white'}`}>Sign In</Link>
            <Link to="/register" className={`text-sm px-4 py-1.5 font-semibold rounded-full transition-all hover:scale-[1.02] ${
              scrolled ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-white text-black'
            }`}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════
           SECTION 1 — HERO (BLACK)
           ══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[75vh] flex items-center bg-[#000000] overflow-hidden">
        <img src="/images/hero-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/80 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.06] mb-8">
                <Zap size={14} className="text-teal-400" />
                <span className="text-[13px] text-slate-400 font-medium">Expert-Built Investment Intelligence</span>
              </div>
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] mb-6">
                <span className="text-white block">Invest smarter</span>
                <span className="text-white block">with your</span>
                <span className="l-gradient-text-dark block">expert team.</span>
              </h1>
              <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-lg">
                Track JSE, global markets, forex, and crypto. Get expert analysis, real-time alerts, and a dedicated trading assistant — all in one dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-white text-black font-bold rounded-full text-[15px] transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,255,255,0.1)]">
                  Start Free Trial <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link to="/releases" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white font-semibold rounded-full text-[15px] transition-all">
                  What's New in v2.0
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative">
              {/* Dashboard mockup always shown; brand video overlays when available (AC3/4) */}
              <DashboardMockup marketData={marketData} loading={loadingMarkets} />
              <div className="absolute inset-0">
                <BrandVideoHero />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 2 — AI FEATURES (WHITE)
           ══════════════════════════════════════════════════════════ */}
      <section className="bg-white pt-28 pb-14 md:pt-36 md:pb-16">
        <Reveal className="max-w-6xl mx-auto px-6 mb-12">
          <p className="text-sm font-semibold tracking-widest uppercase text-gray-400 mb-4">Platform</p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 leading-[1.05] mb-4">
            Everything you need.<br />
            <span className="l-gradient-text-light">Nothing you don't.</span>
          </h2>
          <p className="text-lg text-gray-500 max-w-xl">
            Professional-grade tools for beginners and experienced investors alike.
          </p>
        </Reveal>

        {/* AI Chat showcase — dark inset for contrast */}
        <div className="relative bg-[#000000] rounded-none md:rounded-3xl md:mx-6 lg:mx-auto lg:max-w-6xl overflow-hidden mb-16">
          <img src="/images/ai-brain.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 pointer-events-none" />
          <div className="relative px-6 py-14 md:px-12 md:py-16">
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-5">
                    <Brain size={18} className="text-teal-400" />
                  </div>
                  <h3 className="text-3xl font-black text-white mb-3">Expert Chat</h3>
                  <p className="text-slate-400 leading-relaxed mb-6">
                    Ask about any asset and get instant analysis using your portfolio context, real market data, and your strategy rules. Backed by our expert research desk.
                  </p>
                  <div className="space-y-4">
                    {[
                      { icon: MessageCircle, t: 'Floating chat accessible from any page' },
                      { icon: Zap, t: 'Real-time streaming with tool execution pills' },
                      { icon: Brain, t: 'Multi-persona: Market Analyst & Investment Banker' },
                    ].map((f) => (
                      <div key={f.t} className="flex items-center gap-3">
                        <f.icon size={14} className="text-teal-400 flex-shrink-0" />
                        <span className="text-sm text-slate-400">{f.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <AIChatMockupLight />
              </div>
            </Reveal>
          </div>
        </div>

        {/* Screener showcase */}
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Screener mockup — light */}
              <div className="order-2 lg:order-1">
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-xl shadow-gray-200/50">
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                    <Search size={12} className="ml-2 text-indigo-500" />
                    <span className="text-[11px] text-gray-400">Market Screener</span>
                  </div>
                  <div className="p-3 space-y-1.5">
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {['JSE', 'US', 'Forex', 'RSI < 30', 'Vol > 1M'].map((f) => (
                        <span key={f} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">{f}</span>
                      ))}
                    </div>
                    {[
                      { sym: 'AGL.JSE', name: 'Anglo American', price: 'R542.30', ch: '+2.1%', sig: 'BUY', c: 'emerald' },
                      { sym: 'AAPL.US', name: 'Apple', price: '$198.50', ch: '+0.8%', sig: 'HOLD', c: 'amber' },
                      { sym: 'EURUSD', name: 'EUR/USD', price: '1.0845', ch: '-0.3%', sig: 'SELL', c: 'red' },
                      { sym: 'NPN.JSE', name: 'Naspers', price: 'R3,210', ch: '+1.5%', sig: 'BUY', c: 'emerald' },
                    ].map((r) => (
                      <div key={r.sym} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5">
                        <div className="flex-1 min-w-0">
                          <span className="text-[11px] font-bold text-gray-900">{r.sym}</span>
                          <span className="text-[10px] text-gray-400 ml-2">{r.name}</span>
                        </div>
                        <span className="text-xs text-gray-900 font-medium">{r.price}</span>
                        <span className={`text-[10px] font-medium text-${r.c}-600`}>{r.ch}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold bg-${r.c}-50 text-${r.c}-600`}>{r.sig}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-5">
                  <Globe size={18} className="text-indigo-600" />
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-3">Multi-Market Access</h3>
                <p className="text-gray-500 leading-relaxed mb-6">
                  JSE, US, UK, EU markets, forex, crypto — all in one place. Screen by technical indicators, fundamentals, or expert, data-driven signals.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Globe, t: '73 exchanges across all asset classes' },
                    { icon: BarChart3, t: 'RSI, MACD, Bollinger — full indicator suite' },
                    { icon: Eye, t: 'Real-time prices with WebSocket streaming' },
                  ].map((f) => (
                    <div key={f.t} className="flex items-center gap-3">
                      <f.icon size={14} className="text-indigo-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{f.t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 3 — ALERTS & MONITORING (BLACK)
           ══════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#000000] pt-28 pb-14 md:pt-36 md:pb-16 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-amber-500/[0.03] rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5">
                  <Bell size={18} className="text-amber-400" />
                </div>
                <h3 className="text-3xl font-black text-white mb-3">Investment Banker</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Price alerts, expert signals, portfolio updates, and the Investment Banker — all pushing real-time notifications to your dashboard and MT5 terminal.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Bell, t: 'Price, volume, and indicator alerts' },
                    { icon: Landmark, t: 'Investment Banker dual-persona engine' },
                    { icon: Target, t: 'Automated lead discovery and validation' },
                  ].map((f) => (
                    <div key={f.t} className="flex items-center gap-3">
                      <f.icon size={14} className="text-amber-400 flex-shrink-0" />
                      <span className="text-sm text-slate-400">{f.t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl shadow-teal-500/[0.05]">
                <img src="/images/banker-monitoring.png" alt="Portfolio monitoring visualization" className="w-full h-auto" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Feature grid — light cards floating on dark */}
        <Reveal className="max-w-6xl mx-auto px-6 mt-14">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { icon: Briefcase, title: 'Portfolio Tracking', desc: 'Multiple portfolios with live P&L analytics', color: 'teal' },
              { icon: Library, title: 'Strategy Library', desc: 'Extract strategies from YouTube videos', color: 'violet' },
              { icon: Lightbulb, title: 'Self-Learning Expertise', desc: 'Correct it once, it remembers forever', color: 'amber' },
              { icon: Shield, title: 'Secure & Private', desc: 'JWT auth, encrypted data, API key isolation', color: 'slate' },
              { icon: BarChart3, title: 'Performance Metrics', desc: 'Win rate, drawdown, and trade analytics', color: 'indigo' },
              { icon: Globe, title: '73 Exchanges', desc: 'JSE, NYSE, LSE, FOREX, Crypto — connected', color: 'teal' },
            ].map((f) => (
              <div key={f.title} className="l-card-lift rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6">
                <f.icon size={20} className={`text-${f.color}-400 mb-3`} />
                <h4 className="text-sm font-bold text-white mb-1">{f.title}</h4>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 4 — THE ONLY PLATFORM (WARM CREAM)
           ══════════════════════════════════════════════════════════ */}
      <section className="bg-[#faf7f2] pt-28 pb-14 md:pt-36 md:pb-16">
        <Reveal className="max-w-5xl mx-auto px-6 mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900/[0.04] border border-gray-900/[0.08] mb-6">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
            <span className="text-[13px] text-gray-500 font-medium">No competitor offers this</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-gray-900 leading-[1.05] mb-4">
            The only platform<br />
            <span className="l-gradient-text-light">that does all of this.</span>
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl">
            We researched 20+ platforms globally. Not one combines what my investments delivers in a single product.
          </p>
        </Reveal>

        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: Landmark,
                title: 'An expert eye on your positions every 30 minutes',
                desc: 'The Investment Banker runs dual-persona analysis on a schedule — checking P&L, support/resistance proximity, and sector context. No other platform has automated portfolio oversight.',
                tag: 'Investment Banker',
                img: '/images/banker-monitoring.png',
              },
              {
                icon: Library,
                title: 'Strategy extraction from a YouTube video',
                desc: 'Paste a URL. We download, extract frames, transcribe audio, and produce a scored, structured trading strategy. This feature exists nowhere else.',
                tag: 'Strategy Extraction',
                img: '/images/strategy-extraction.png',
              },
              {
                icon: Lightbulb,
                title: 'An expert that learns from your corrections',
                desc: 'Correct the Expert once — it updates its knowledge at user, domain, symbol, and strategy levels. Next time, it gets it right. No other trading platform does this.',
                tag: 'Self-Learning',
                img: '/images/self-learning.png',
              },
              {
                icon: Brain,
                title: 'MCP tools that plug into Claude Desktop & Cursor',
                desc: '13 tools exposed via Model Context Protocol. Use your trading data directly inside Claude Desktop, Cursor, or Claude Code. A first in the industry.',
                tag: 'MCP Integration',
                img: '/images/ai-brain.png',
              },
            ].map((item) => (
              <Reveal key={item.title}>
                <div className="l-card-lift group rounded-2xl bg-white border border-gray-200/60 overflow-hidden h-full shadow-sm hover:shadow-md hover:border-gray-300/60 transition-all">
                  <div className="h-40 overflow-hidden bg-black">
                    <img src={item.img} alt={item.tag} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" loading="lazy" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-lg bg-teal-50 border border-teal-200/60 flex items-center justify-center">
                        <item.icon size={16} className="text-teal-600" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-teal-600/70">{item.tag}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* JSE + Global banner */}
          <Reveal className="mt-5">
            <div className="rounded-2xl bg-gradient-to-r from-teal-100/60 to-indigo-100/60 border border-gray-200/60 p-7 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Globe size={18} className="text-teal-600" />
                <h3 className="text-lg font-bold text-gray-900">All of this with JSE + global coverage</h3>
              </div>
              <p className="text-sm text-gray-500 max-w-xl mx-auto">
                73 exchanges. JSE, NYSE, LSE, forex, crypto — while every competitor is locked to US markets only. my investments was built for South African investors who trade globally.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 5 — PRICING (BLACK)
           ══════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#000000] pt-28 pb-14 md:pt-36 md:pb-16 overflow-hidden">
        <img src="/images/pricing-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80 pointer-events-none" />

        <Reveal className="relative max-w-5xl mx-auto px-6 mb-10">
          <p className="text-sm font-semibold tracking-widest uppercase text-slate-600 mb-4">Pricing</p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight text-white mb-4">
            One subscription.<br /><span className="l-gradient-text-dark">An entire expert team.</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-xl">Competitors charge $254/month for a single scanner. You get a full ecosystem for less.</p>
        </Reveal>

        {/* Pricing cards — light cards floating on dark bg */}
        <Reveal className="relative max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free Trial */}
            <div className="rounded-3xl bg-white/[0.04] border border-white/[0.08] p-8">
              <h3 className="text-2xl font-bold text-white mb-1">Free Trial</h3>
              <p className="text-slate-500 text-sm mb-6">Explore everything</p>
              <div className="mb-8">
                <span className="text-5xl font-black text-white">14</span>
                <span className="text-slate-500 ml-1">days free</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Full platform access', 'JSE + global markets', 'Expert Chat (10 messages)', 'Expert analysis (5 reports)', '1 portfolio + 5 alerts'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-400">
                    <Check size={14} className="text-teal-400 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block w-full text-center py-3.5 rounded-full bg-white text-black font-semibold transition-all hover:bg-gray-100 text-sm">
                Start Free Trial
              </Link>
            </div>
            {/* Pro */}
            <div className="rounded-3xl bg-gradient-to-b from-teal-500/[0.12] to-white/[0.03] border-2 border-teal-500/30 p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-teal-500 to-indigo-500 text-[11px] font-bold text-white">MOST POPULAR</div>
              <h3 className="text-2xl font-bold text-white mb-1">Pro</h3>
              <p className="text-slate-500 text-sm mb-6">Your expert analyst</p>
              <div className="mb-2 h-12 flex items-baseline">
                {proPrice ? (
                  <>
                    <span className="text-5xl font-black text-white">{proPrice.big}</span>
                    <span className="text-slate-500 ml-1">{proPrice.suffix}</span>
                  </>
                ) : (
                  <span className="inline-block h-10 w-32 bg-white/5 animate-pulse rounded" />
                )}
              </div>
              <p className="text-xs text-teal-400 font-medium mb-8">Cheaper than Trade Ideas ($127) with 5x more features</p>
              <ul className="space-y-3 mb-8">
                {['Real-time data (73 exchanges)', 'Expert Chat (unlimited)', 'Expert analysis (50/month)', 'Trading Analyst + MQL5 EA', 'Full screener + export', 'Unlimited alerts & portfolios', 'Strategy extraction', 'MCP integration (13 tools)'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-300">
                    <Check size={14} className="text-teal-400 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block w-full text-center py-3.5 rounded-full bg-white text-black font-bold transition-all hover:bg-gray-100 hover:shadow-lg text-sm">
                Get Started
              </Link>
            </div>
            {/* Premium */}
            <div className="rounded-3xl bg-gradient-to-b from-indigo-500/[0.12] to-white/[0.03] border-2 border-indigo-500/30 p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-[11px] font-bold text-white">FULL EXPERT TEAM</div>
              <h3 className="text-2xl font-bold text-white mb-1">Premium</h3>
              <p className="text-slate-500 text-sm mb-6">Your expert investment team</p>
              <div className="mb-2 h-12 flex items-baseline">
                {premiumPrice ? (
                  <>
                    <span className="text-5xl font-black text-white">{premiumPrice.big}</span>
                    <span className="text-slate-500 ml-1">{premiumPrice.suffix}</span>
                  </>
                ) : (
                  <span className="inline-block h-10 w-32 bg-white/5 animate-pulse rounded" />
                )}
              </div>
              <p className="text-xs text-indigo-400 font-medium mb-8">Replaces $343/month in competing subscriptions</p>
              <ul className="space-y-3 mb-8">
                {['Everything in Pro', 'Investment Banker (24/7 monitoring)', 'Self-learning insights', 'MCP integration (13 tools)', 'Multi-channel alerts (app + EA)', 'Leads management', 'Unlimited expert analyses', 'Priority support'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-300">
                    <Check size={14} className="text-teal-400 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block w-full text-center py-3.5 rounded-full bg-white text-black font-bold transition-all hover:bg-gray-100 hover:shadow-lg text-sm">
                Get Started
              </Link>
            </div>
          </div>
        </Reveal>

        {/* Competitor comparison */}
        <Reveal className="relative max-w-4xl mx-auto px-6 mt-10">
          <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-8">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-6 text-center">What you'd pay elsewhere</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { name: 'Trade Ideas', price: '$254/mo', feature: 'Signal scanner only' },
                { name: 'TrendSpider', price: '$137/mo', feature: 'Auto-analysis only' },
                { name: 'TradingView', price: '$60/mo', feature: 'Charts + screener' },
                { name: 'FinChat', price: '$64/mo', feature: 'Research chat only' },
              ].map((c) => (
                <div key={c.name}>
                  <p className="text-xs text-slate-600 mb-1">{c.name}</p>
                  <p className="text-lg font-black text-white">{c.price}</p>
                  <p className="text-[11px] text-slate-600">{c.feature}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-white/[0.06] text-center space-y-3">
              <p className="text-sm text-slate-500">Combined: <span className="font-bold text-white">$515/month</span> for a fraction of what my investments offers at {premiumPrice ? (
                <span className="font-bold text-teal-400">{premiumPrice.big}{premiumPrice.suffix}</span>
              ) : (
                <span className="inline-block h-3 w-16 bg-white/5 animate-pulse rounded align-middle" />
              )}</p>
              <p className="text-sm text-slate-600">Need team pricing? <a href="mailto:sales@marome-investments.co.za" className="text-teal-400 hover:text-teal-300 transition-colors">Contact us for enterprise plans →</a></p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 6 — B2B / ENTERPRISE (WHITE)
           ══════════════════════════════════════════════════════════ */}
      <section className="bg-white pt-28 pb-14 md:pt-36 md:pb-16">
        <Reveal className="max-w-6xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest uppercase text-gray-400 mb-4">Enterprise</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 mb-4">
            Built for financial services firms.
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mb-10">
            White-label the platform under your brand. Manage teams, control entitlements, and negotiate custom pricing — all from a self-service admin portal.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Building,
                title: 'Multi-Tenant Org Management',
                desc: 'Create organisations with roles (admin, finance manager, lead analyst, developer). Invite members by email or enable domain auto-join. Full KYC and compliance workflows built in.',
              },
              {
                icon: Users,
                title: 'Custom Pricing Agreements',
                desc: 'Per-seat or flat-rate billing. Finance Manager approval workflows. Live usage cost estimates. Entitlement overrides per org — control exchange access, analysis request limits, and feature flags.',
              },
              {
                icon: Palette,
                title: 'White-Label & Custom Domains',
                desc: 'Your logo, your colours, your domain. Members see your brand from login to dashboard. CNAME-based custom domains with automatic SSL provisioning via Cloudflare.',
              },
            ].map((item) => (
              <div key={item.title} className="l-card-lift rounded-2xl bg-white border border-gray-200/60 p-6 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200/60 flex items-center justify-center mb-4">
                  <item.icon size={18} className="text-teal-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a
              href="mailto:sales@marome-investments.co.za"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-full text-sm transition-all hover:bg-gray-800 hover:shadow-lg"
            >
              Contact Sales <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </section>

      {/* ══════════════════════════════════════════════════════════
           SECTION 7 — CTA (BLACK)
           ══════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#000000] pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-teal-500/[0.03] to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-teal-500/[0.06] rounded-full blur-[150px] pointer-events-none" />
        <Reveal className="relative max-w-2xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-8">
            <GoldMLogo size={48} />
          </div>
          <h2 className="text-5xl md:text-7xl font-black tracking-tight text-white mb-6">
            Ready to invest<br /><span className="l-gradient-text-dark">smarter?</span>
          </h2>
          <p className="text-lg text-slate-500 mb-10">Join investors using expert-built insights to make better decisions.</p>
          <Link to="/register" className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold rounded-full text-[15px] transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(255,255,255,0.1)]">
            Create Free Account <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </Reveal>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="bg-[#000000] border-t border-white/[0.04] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <GoldMLogo size={22} />
            <span className="text-sm font-semibold text-slate-400">my investments</span>
          </div>
          <p className="text-[13px] text-slate-700">© 2026 my investments. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/releases" className="text-[13px] text-slate-600 hover:text-white transition-colors">What's New</Link>
            <Link to="/faq" className="text-[13px] text-slate-600 hover:text-white transition-colors">FAQ</Link>
            <Link to="/mcp" className="text-[13px] text-slate-600 hover:text-white transition-colors">MCP Integration</Link>
            <a href="#" className="text-[13px] text-slate-600 hover:text-white transition-colors">Privacy</a>
            <a href="#" className="text-[13px] text-slate-600 hover:text-white transition-colors">Terms</a>
            <a href="#" className="text-[13px] text-slate-600 hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
